'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import NewsCard from '@/components/news/NewsCard'
import { NewsCardSkeleton } from '@/components/NewsCardSkeleton'
import { getApiBaseUrl } from '@/lib/config'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import type { NewsWithImageUrl } from '@/components/news/NewsCard'
import { getImageUrl } from '@/lib/utils'
import type { News } from '@/lib/types'

interface CategoryNewsAutoScrollProps {
  categoryId: number
  categorySlug: string
  initialNews: NewsWithImageUrl[]
  startPage: number
  initialHasMore: boolean
  initialCursor: string | null
}

function resolveClientNewsImage(item: News): NewsWithImageUrl {
  const path = item.image?.trim()
  return {
    ...item,
    imageUrl: path ? getImageUrl(path, IMAGE_PRESETS.listCard) : null,
  }
}

export default function CategoryNewsAutoScroll({
  categoryId,
  categorySlug,
  initialNews,
  startPage,
  initialHasMore,
  initialCursor,
}: CategoryNewsAutoScrollProps) {
  const [news, setNews] = useState<NewsWithImageUrl[]>(initialNews)
  const [page, setPage] = useState(startPage + 1)
  const [hasMore, setHasMore] = useState(initialHasMore)
  const [cursor, setCursor] = useState(initialCursor)
  const [loading, setLoading] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const fetchLockRef = useRef(false)

  const fetchPage = useCallback(
    async (pageNum: number) => {
      if (fetchLockRef.current || !hasMore) return
      fetchLockRef.current = true
      setLoading(true)
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/news/category/${categoryId}/pagination?page=${pageNum}${
            cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
          }`,
          { cache: 'no-store' },
        )
        if (!response.ok) {
          setHasMore(false)
          return
        }
        const data = await response.json()
        const batch = Array.isArray(data.news) ? (data.news as News[]) : []
        if (batch.length === 0) {
          setHasMore(false)
          return
        }
        setNews((prev) => {
          const seen = new Set(prev.map((item) => item.id))
          const next = batch.filter((item) => !seen.has(item.id)).map(resolveClientNewsImage)
          return next.length > 0 ? [...prev, ...next] : prev
        })
        const nextPage = pageNum + 1
        setPage(nextPage)
        setHasMore(data.hasMore === true)
        setCursor(typeof data.nextCursor === 'string' ? data.nextCursor : null)
      } catch {
        setHasMore(false)
      } finally {
        setLoading(false)
        fetchLockRef.current = false
      }
    },
    [categoryId, cursor, hasMore],
  )

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || fetchLockRef.current || !hasMore) return
        void fetchPage(page)
      },
      { threshold: 0.1, rootMargin: '160px' },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [fetchPage, hasMore, page])

  if (news.length === 0) return null

  return (
    <div className="min-w-0" aria-label={`${categorySlug} haberleri`}>
      <div className="grid min-w-0 grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4">
        {news.map((item) => (
          <NewsCard key={item.id} item={item} variant="grid" showDate={false} plain />
        ))}
        {loading
          ? Array.from({ length: 4 }, (_, index) => (
              <NewsCardSkeleton key={`sk-${index}`} plain showDate={false} />
            ))
          : null}
      </div>

      {!hasMore && news.length > 0 ? (
        <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">Tüm haberler yüklendi</p>
      ) : null}

      <div ref={sentinelRef} className="h-1" aria-hidden />
    </div>
  )
}
